import { MobXProviderContext } from 'mobx-react';
import { useVideoPermission } from 'Modules/softphone/hooks/useVideoPermission';
import { CallManager } from 'Modules/softphone/services/CallManager';
import { EventService } from 'Modules/softphone/services/EventService';
import { WebRTCService } from 'Modules/softphone/services/WebRTCService';
import {
  useSoftphoneStore,
  selectPermissions,
} from 'Modules/softphone/store';
import type { CallPermissions, CallSession } from 'Modules/softphone/types';
import { softphoneCallControlsLogger as logger } from 'Modules/softphone/utils/logger';
import { useCallback, useContext } from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { searchPersonBySip } from 'Utils/searchPersonBySip';
import { useShallow } from 'zustand/react/shallow';

export const useSoftphoneCallControls = () => {
  const rootStore = useContext(MobXProviderContext) as RootStoreProps | null;

  const {
    isKeypadActive,
    isVideoActive,
    isTransferActive,
    isMergeActive,
    isMerging,
    permissions,
    toggleKeypad,
    toggleTransfer,
    setIsMerging,
    currentCall,
  } = useSoftphoneStore(
    useShallow((state): {
      isKeypadActive: boolean;
      isVideoActive: boolean;
      isTransferActive: boolean;
      isMergeActive: boolean;
      isMerging: boolean;
      permissions: CallPermissions;
      toggleKeypad: () => void;
      toggleTransfer: () => void;
      setIsMerging: (isMerging: boolean) => void;
      currentCall: CallSession | null;
      currentCallId: string | null;
    } => {
      const currentCall = state.currentCallId
        ? state.activeCalls.get(state.currentCallId) ?? null
        : null;

      return {
        isKeypadActive: state.isKeypadActive,
        isVideoActive: state.isVideoActive,
        isTransferActive: state.isTransferActive,
        isMergeActive: state.isMergeActive,
        isMerging: state.isMerging,
        permissions: selectPermissions(state),
        toggleKeypad: state.toggleKeypad,
        toggleTransfer: state.toggleTransfer,
        setIsMerging: state.setIsMerging,
        currentCall,
        currentCallId: state.currentCallId,
      };
    })
  );

  const webrtcService = WebRTCService.getInstance();
  const callManager = CallManager.getInstance();

  const isMuted = currentCall?.isMuted ?? false;
  const isOnHold = currentCall?.isOnHold ?? false;

  const { isVideoDisabled, videoTooltip } = useVideoPermission();
  const isVideoButtonActive = permissions.canUseVideo && !isVideoDisabled;

  const handleMute = useCallback(() => {
    // Get fresh state to avoid stale closures
    const state = useSoftphoneStore.getState();
    const callId = state.currentCallId;

    if (!selectPermissions(state).canMute) {
      logger.warn('[SoftphoneCallControls] Cannot mute: not allowed');
      return;
    }

    if (!callId) {
      logger.warn('[SoftphoneCallControls] Cannot mute: no active call');
      return;
    }

    const call = state.activeCalls.get(callId);
    const currentMuted = call?.isMuted ?? false;
    const newMutedState = !currentMuted;

    const isInConference = Boolean(call?.conferenceId);
    const conference = state.activeConference;

    logger.info('[SoftphoneCallControls] Toggle mute', {
      from: currentMuted,
      to: newMutedState,
      callId,
      isInConference,
    });

    // During a conference the individual call's peer connection is terminated
    // after REFER. Audio flows through the room session, so we must mute its
    // peer connection instead (keyed by roomGuid in WebRTCService).
    const muteTarget =
      isInConference && conference?.roomGuid ? conference.roomGuid : callId;

    const audioTrackToggled = newMutedState
      ? webrtcService.muteLocalAudio(muteTarget)
      : webrtcService.unmuteLocalAudio(muteTarget);

    if (audioTrackToggled) {
      state.updateCall(callId, { isMuted: newMutedState });

      // Log event
      EventService.getInstance().logRtpEvent(
        'outgoing',
        newMutedState ? 'Audio muted' : 'Audio unmuted',
        { callId, isInConference, muteTarget }
      );

      // Send GA event
      const gaEvent = newMutedState ? 'on_call_mute' : 'on_call_unmute';
      pushToGTMDataLayer(gaEvent, { call_id: callId });
    }
  }, [webrtcService]);

  const handleHold = useCallback(async () => {
    const state = useSoftphoneStore.getState();
    const callId = state.currentCallId;

    if (!selectPermissions(state).canHold) {
      logger.warn('Cannot hold: not allowed', {
        permissions: selectPermissions(state),
        callId,
      });
      return;
    }

    if (!callId) {
      logger.warn('Cannot hold: no active call');
      return;
    }

    const call = state.activeCalls.get(callId);
    if (!call) {
      logger.warn('Cannot hold: call not found', { callId });
      return;
    }

    try {
      await callManager.toggleHold(callId);
    } catch (error) {
      logger.error('Failed to toggle hold', {
        callId,
        error: (error as Error).message,
      });
    }
  }, [callManager]);

  const handleKeypad = () => {
    const state = useSoftphoneStore.getState();
    const callId = state.currentCallId;

    logger.info('[SoftphoneCallControls] Toggle keypad', {
      from: isKeypadActive,
      to: !isKeypadActive,
      canUseKeypad: permissions.canUseKeypad,
    });

    // Send analytics event when opening keypad
    if (!isKeypadActive && callId) {
      pushToGTMDataLayer('in_call_keypad_opened', { call_id: callId });
    }

    toggleKeypad();
  };

  const handleVideo = useCallback(async () => {
    const state = useSoftphoneStore.getState();
    const callId = state.currentCallId;

    if (!callId) {
      logger.warn('[SoftphoneCallControls] Cannot start video: no active call');
      return;
    }

    const call = state.activeCalls.get(callId);
    if (!call) {
      logger.warn('[SoftphoneCallControls] Cannot start video: call not found');
      return;
    }

    let personId = call.personId;

    // If no personId, try to resolve it from the SIP session (outbound calls via dialpad)
    if (!personId && call.session) {
      const remoteUri =
        call.direction === 'outbound'
          ? (
              call.session as {
                remoteIdentity?: { uri?: { user?: string; host?: string } };
              }
            ).remoteIdentity?.uri
          : call.session.remoteIdentity?.uri;

      if (remoteUri?.user && remoteUri?.host) {
        const sipUri = `${remoteUri.user}@${remoteUri.host}`;
        logger.info('[SoftphoneCallControls] Resolving personId from SIP URI', {
          sipUri,
        });

        try {
          const person = await searchPersonBySip(sipUri);
          if (person?.sourceId) {
            personId = person.sourceId.toString();
            // Update the call session so we don't need to resolve again
            state.updateCall(callId, { personId });
            logger.info('[SoftphoneCallControls] Resolved personId from SIP', {
              personId,
              sipUri,
            });
          }
        } catch (error) {
          logger.warn(
            '[SoftphoneCallControls] Failed to resolve personId from SIP',
            {
              error: (error as Error).message,
            }
          );
        }
      }
    }

    if (!personId) {
      logger.warn(
        '[SoftphoneCallControls] Cannot start video: no personId (external contact)'
      );
      return;
    }

    const conversationStore = rootStore?.conversation;
    if (!conversationStore) {
      logger.error(
        '[SoftphoneCallControls] Cannot start video: conversationStore not available'
      );
      return;
    }

    logger.info('[SoftphoneCallControls] Initiating video escalation', {
      callId,
      personId,
    });

    // Send analytics event
    pushToGTMDataLayer('on_call_video', { call_id: callId });

    try {
      // Create conversation and start video conference
      // The openVideoPopup in ConversationStore will automatically hang up the active call
      const { data } = await conversationStore.loadOrCreateConversationWithPost(
        personId
      );
      await conversationStore.postConferenceByConversationId(
        data.id.toString()
      );

      // Hang up the active softphone call — can't be in both a call and a video conference
      try {
        await callManager.hangUp(callId);
      } catch (hangUpError) {
        logger.warn(
          '[SoftphoneCallControls] Failed to hang up call after video escalation',
          {
            error: (hangUpError as Error).message,
            callId,
          }
        );
      }

      logger.info('[SoftphoneCallControls] Video escalation initiated', {
        callId,
        conversationId: data.id,
      });
    } catch (error) {
      logger.error('[SoftphoneCallControls] Failed to initiate video call', {
        error: (error as Error).message,
        callId,
      });
    }
  }, [rootStore, callManager]);

  const handleTransfer = () => {
    logger.info('[SoftphoneCallControls] Toggle transfer', {
      from: isTransferActive,
      to: !isTransferActive,
      canTransfer: permissions.canTransfer,
    });
    toggleTransfer();
  };

  const handleMerge = useCallback(async () => {
    const state = useSoftphoneStore.getState();

    if (!selectPermissions(state).canMerge) {
      logger.warn('[SoftphoneCallControls] Cannot merge: not allowed');
      pushToGTMDataLayer('call_merge_unavailable');
      return;
    }

    if (state.isMerging) {
      logger.warn('[SoftphoneCallControls] Merge already in progress');
      return;
    }

    logger.info('[SoftphoneCallControls] Initiating merge');
    pushToGTMDataLayer('call_merge_initiated');
    setIsMerging(true);

    try {
      await callManager.mergeCallsToConference();
      // call_merge_success is emitted by ConferenceHandler
    } catch (error) {
      logger.error('[SoftphoneCallControls] Merge failed', {
        error: (error as Error).message,
      });
      // call_merge_failed is emitted by ConferenceHandler
    } finally {
      setIsMerging(false);
    }
  }, [callManager, setIsMerging]);

  /**
   * Initiate a new call - delegates to CallManager.
   */
  const handleCall = useCallback(
    async (phoneNumber: string) => {
      if (!phoneNumber) {
        logger.warn(
          '[SoftphoneCallControls] Cannot call: no phone number provided'
        );
        return;
      }

      logger.info('[SoftphoneCallControls] Initiating call via CallManager', {
        phoneNumber,
      });

      try {
        await callManager.makeCall(phoneNumber);
        pushToGTMDataLayer('call_initiated', { phone_number: phoneNumber });
      } catch (error) {
        logger.error('[SoftphoneCallControls] Error initiating call', {
          error: (error as Error).message,
        });
      }
    },
    [callManager]
  );

  /**
   * End current call - delegates to CallManager for single source of truth.
   * All call termination logic (SIP bye/cancel, audio cleanup, store updates)
   * is handled centrally by the manager.
   */
  const handleEndCall = useCallback(async () => {
    const state = useSoftphoneStore.getState();
    const callId = state.currentCallId;

    if (!callId) {
      logger.warn('[SoftphoneCallControls] Cannot end call: no active call');
      if (state.activeCalls.size === 0) {
        state.resetCallControls();
      }
      return;
    }

    logger.info('[SoftphoneCallControls] Ending call via CallManager', {
      callId,
    });

    try {
      await callManager.hangUp(callId);
    } catch (error) {
      logger.error('[SoftphoneCallControls] Error ending call', {
        error: (error as Error).message,
      });
    }

    // Reset UI controls after call ends ONLY if no more active calls
    // Permissions are derived automatically via selectPermissions selector
    const updatedState = useSoftphoneStore.getState();
    if (updatedState.activeCalls.size === 0) {
      logger.info(
        '[SoftphoneCallControls] No more active calls, resetting controls'
      );
      updatedState.resetCallControls();
    } else {
      logger.info(
        '[SoftphoneCallControls] Still have active calls, not resetting controls',
        {
          activeCallsCount: updatedState.activeCalls.size,
          currentCallId: updatedState.currentCallId,
        }
      );
    }
  }, [callManager]);

  return {
    // States
    isMuted,
    isOnHold,
    isKeypadActive,
    isVideoActive,
    isTransferActive,
    isMergeActive,
    isMerging,
    permissions,
    isVideoButtonActive,
    videoTooltip,

    // Handlers
    handleMute,
    handleHold,
    handleKeypad,
    handleVideo,
    handleTransfer,
    handleMerge,
    handleCall,
    handleEndCall,
  };
};
